import { useCallback, useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { User, Wallet, Save, Plus, Trash2, BadgeInfo, AlertTriangle, PhoneCall, Printer, Lock, History, ChevronDown, Shield, Bell, UserX, UserCheck } from "lucide-react";
import BankAccountsEditor from "@/components/BankAccountsEditor";
import SessionHistorySection from "@/components/profile/SessionHistorySection";
import DisableAccountDialog from "@/components/user-management/DisableAccountDialog";
import NotificationPrefsSection from "@/components/profile/NotificationPrefsSection";
import MessagingPrefsSection from "@/components/profile/MessagingPrefsSection";
import api, { formatDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useAccess } from "@/lib/access";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import DatePicker from "@/components/DatePicker";
import SalaryDialog from "@/components/SalaryDialog";
import { INDONESIAN_BANKS } from "@/lib/banks";
import { INDONESIA_CITIES } from "@/lib/indonesia";
import { DIVISION_COLOR } from "@/lib/constants";

const MARITAL_OPTIONS = ["Belum Menikah", "Menikah", "Cerai Hidup", "Cerai Mati"];
// Fee-free banks are derived at runtime from Settings → Company Bank Accounts (see
// useEffect below). Kept as a Set state so BOD/FAT-L2 can control which banks
// don't trigger the admin-fee warning by simply managing the Company Bank list.

// Fields the user themselves can edit (rest are HR/Director only)
const USER_EDITABLE = new Set([
  "bank_name", "bank_account_no",
  "emergency_contact_name", "emergency_contact_phone", "emergency_contact_address",
]);

// Sections for logical grouping
const SECTION_PERSONAL = "personal";
const SECTION_FAMILY = "family";
const SECTION_EDU = "edu";
const SECTION_BANK = "bank";
const SECTION_EMERGENCY = "emergency";

const SECTION_STRUCTURE = "structure";
const PROFILE_FIELDS_META = [
  // Structure / Organization
  { key: "position", label: "Position", type: "position-picker", placeholder: "Pilih posisi dari daftar…", section: SECTION_STRUCTURE },
  { key: "reports_to", label: "Melapor Kepada (Atasan)", type: "user-picker", section: SECTION_STRUCTURE },
  // Personal
  { key: "work_start_date", label: "Tanggal Masuk Kerja", type: "date", section: SECTION_PERSONAL },
  { key: "birth_date", label: "Tanggal Lahir", type: "date", section: SECTION_PERSONAL },
  { key: "blood_type", label: "Golongan Darah", type: "select", options: ["A", "B", "AB", "O", "A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"], section: SECTION_PERSONAL },
  { key: "gender", label: "Jenis Kelamin", type: "select", options: [{ v: "L", l: "Laki-laki" }, { v: "P", l: "Perempuan" }], section: SECTION_PERSONAL },
  { key: "religion", label: "Agama", type: "select", options: ["Islam", "Kristen Protestan", "Katolik", "Hindu", "Buddha", "Konghucu"], section: SECTION_PERSONAL },
  { key: "phone", label: "No HP", type: "text", placeholder: "+62...", section: SECTION_PERSONAL },
  { key: "ktp", label: "No KTP (NIK)", type: "text", section: SECTION_PERSONAL },
  { key: "npwp", label: "No NPWP", type: "text", section: SECTION_PERSONAL },
  { key: "nitku", label: "NITKU", type: "text", placeholder: "Nomor Identitas Tempat Kegiatan Usaha", section: SECTION_PERSONAL },
  { key: "staff_base", label: "Staff Base (Kota Rekrut)", type: "city", section: SECTION_PERSONAL, placeholder: "Ketik nama kota/kabupaten…" },
  // Family
  { key: "marital_status", label: "Status Pernikahan", type: "select", options: MARITAL_OPTIONS, section: SECTION_FAMILY },
  { key: "kk_number", label: "No Kartu Keluarga (KK)", type: "text", section: SECTION_FAMILY },
  { key: "spouse_name", label: "Nama Pasangan", type: "text", section: SECTION_FAMILY },
  { key: "spouse_birth_date", label: "Tanggal Lahir Pasangan", type: "date", section: SECTION_FAMILY },
  { key: "spouse_ktp", label: "NIK Pasangan", type: "text", section: SECTION_FAMILY },
  { key: "father_name", label: "Nama Ayah", type: "text", section: SECTION_FAMILY },
  { key: "mother_name", label: "Nama Ibu", type: "text", section: SECTION_FAMILY },
  // Education
  { key: "last_education", label: "Pendidikan Terakhir", type: "select", options: ["SD", "SMP", "SMA/SMK", "D1", "D2", "D3", "D4", "S1", "S2", "S3"], section: SECTION_EDU },
  { key: "education_institution", label: "Institusi Pendidikan Terakhir", type: "text", section: SECTION_EDU },
  { key: "diploma_number", label: "Nomor Ijazah", type: "text", section: SECTION_EDU },
  // Bank
  { key: "bank_name", label: "Nama Bank", type: "select", options: INDONESIAN_BANKS, custom: "bank", section: SECTION_BANK },
  { key: "bank_account_no", label: "Nomor Rekening", type: "text", placeholder: "Contoh: 1234567890", section: SECTION_BANK },
  // Emergency
  { key: "emergency_contact_name", label: "Kontak Darurat — Nama (tidak serumah)", type: "text", section: SECTION_EMERGENCY },
  { key: "emergency_contact_phone", label: "Kontak Darurat — No HP", type: "text", section: SECTION_EMERGENCY, placeholder: "+62..." },
  { key: "emergency_contact_address", label: "Kontak Darurat — Alamat", type: "textarea", section: SECTION_EMERGENCY },
];

function computePtkp(maritalStatus, children, gender) {
  const isFemale = (gender || "").toUpperCase() === "P";
  // Karyawati (gender=P): SELALU TK/0 apapun jumlah anak & status pernikahan.
  // Tanggungan hanya diberikan ke suami/kepala keluarga per aturan PPh 21.
  if (isFemale) return "TK/0";
  const validChildren = (children || []).filter((c) => (c?.name || "").trim());
  const n = Math.min(validChildren.length, 3);
  if (maritalStatus === "Menikah") return `K/${n}`;
  return `TK/${n}`;
}

function fmtAuditVal(v) {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "Ya" : "Tidak";
  if (typeof v === "number") return v.toLocaleString("id-ID");
  if (Array.isArray(v)) return `${v.length} entri`;
  if (typeof v === "object") return JSON.stringify(v).slice(0, 40);
  return String(v);
}

export default function ProfileDialog({ open, onOpenChange, targetUserId = null }) {
  const { user } = useAuth();
  const { canPerm } = useAccess();
  // Route via matrix `personnel_manage.update` — sebelumnya hardcoded BOD/HRGA.
  const isAdmin = canPerm(user, "personnel_manage", "update");
  const isAdminEditingOther = !!targetUserId && isAdmin;
  // 149-cw-r150: gate deactivate/activate account (soft-toggle is_active).
  // Sama dgn AdminActionsCell — matrix `user_management.update`. Backend
  // `/api/admin/users/{uid}/disable|enable` validate ulang.
  const canDisableUser = !!targetUserId && canPerm(user, "user_management", "update");
  const profileEndpoint = isAdminEditingOther ? `/admin/users/${targetUserId}/profile` : `/auth/profile`;
  const salaryTargetId = isAdminEditingOther ? targetUserId : null;
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({});
  const [children, setChildren] = useState([]);
  const [saving, setSaving] = useState(false);
  // 149-cw-r173: Hapus staff/user permanen — matrix `user_management.delete`
  // (backend DELETE /admin/users/{uid} validate ulang + blokir hapus diri sendiri).
  const canDeleteUser = !!targetUserId && canPerm(user, "user_management", "delete");
  const [deleteDlg, setDeleteDlg] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [openSalary, setOpenSalary] = useState(false);
  const [pendingBank, setPendingBank] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  // R232 (2026-10-20, user request): aset yang sedang dipinjam/dipegang
  // karyawan ini tetap terlihat di profil — walau menu Data Aset pindah ke
  // grup "Operasional & Aset". Read-only; memakai endpoint assets?pic_id=.
  const [assetsHeld, setAssetsHeld] = useState(null);
  const [auditOpen, setAuditOpen] = useState(false);
  const [sessionOpen, setSessionOpen] = useState(false);
  const [notifPrefsOpen, setNotifPrefsOpen] = useState(false);
  const [teamOptions, setTeamOptions] = useState([]);
  const [positionOptions, setPositionOptions] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);
  const [officePresets, setOfficePresets] = useState([]);
  const [assignedProjectIds, setAssignedProjectIds] = useState([]);
  const [projectFilter, setProjectFilter] = useState("");
  // 149-cw-r151: state utk dialog nonaktifkan akun (alasan wajib).
  const [disableDlg, setDisableDlg] = useState(false);
  const [disableBusy, setDisableBusy] = useState(false);
  const [companyBankNames, setCompanyBankNames] = useState([]);
  const feeFreeSet = useMemo(() => new Set(companyBankNames.map((n) => String(n).trim().toLowerCase())), [companyBankNames]);
  const isCompanyBank = (name) => !!(name && feeFreeSet.has(String(name).trim().toLowerCase()));
  const feeFreeBankLabel = useMemo(() => companyBankNames.slice(0, 3).join(", ") + (companyBankNames.length > 3 ? ` +${companyBankNames.length - 3} lainnya` : ""), [companyBankNames]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/company-bank-accounts");
        setCompanyBankNames(Array.from(new Set((data || []).map((b) => b.bank_name).filter(Boolean))));
      } catch (e) { console.warn("Could not load company bank names", e); setCompanyBankNames([]); }
    })();
  }, [open]);

  // Load team list once when dialog opens so the user-picker (reports_to) can show names
  useEffect(() => {
    if (!open) return;
    let cancel = false;
    (async () => {
      try {
        const [teamRes, posRes, projRes] = await Promise.all([
          api.get("/users/team"),
          api.get("/positions"),
          api.get("/active-projects"),
        ]);
        if (cancel) return;
        setTeamOptions(teamRes.data || []);
        setPositionOptions(posRes.data || []);
        // Split projects: only HEAD/PARENT new_project entries (non-internal, active) are shown
        // in the "Penempatan Project" checkbox list. VOs & Working Orders inherit access from
        // their parent project via the backend (see _visible_project_id_set in server.py).
        // Internal (Head Office / Overhead) → still surfaced as one-click preset buttons.
        const allActive = (projRes.data || []).filter((p) => p.status === "active");
        setProjectOptions(allActive.filter((p) => p.project_type === "new_project"));
        setOfficePresets(allActive.filter((p) => p.project_type === "internal"));
      } catch (e) { /* silent */ }
    })();
    return () => { cancel = true; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get(profileEndpoint);
        if (cancel) return;
        setProfile(data);
        const f = {};
        PROFILE_FIELDS_META.forEach((meta) => { f[meta.key] = data[meta.key] || ""; });
        // Pre-fill position from legacy role_title if position is empty
        if (!f.position && data.role_title) f.position = data.role_title;
        f.bank_admin_fee_accepted = !!data.bank_admin_fee_accepted;
        setForm(f);
        setChildren(Array.isArray(data.children) ? data.children.map((c) => ({ name: c?.name || "", birth_date: c?.birth_date || "", ktp: c?.ktp || "" })) : []);
        setAssignedProjectIds(Array.isArray(data.assigned_project_ids) ? data.assigned_project_ids : []);
      } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
    })();
    return () => { cancel = true; };
  }, [open, profileEndpoint]);

  // Load audit log when admin views another personnel's profile
  const loadAudit = useCallback(async () => {
    if (!isAdminEditingOther) return;
    try {
      const { data } = await api.get("/audit-logs", { params: { target_user_id: targetUserId, limit: 200 } });
      setAuditLogs(data || []);
    } catch (e) { /* silent */ }
  }, [isAdminEditingOther, targetUserId]);
  useEffect(() => { if (open && isAdminEditingOther) loadAudit(); }, [open, isAdminEditingOther, loadAudit]);

  // R232: muat aset yang dipegang karyawan ini saat profil dibuka.
  // Tanpa akses assets.view endpoint mengembalikan scope PIC-only (aman);
  // bila 403 (bukan PIC apapun) → sembunyikan seksi dengan daftar kosong.
  useEffect(() => {
    if (!open) return undefined;
    const uid = targetUserId || user?.id;
    if (!uid) return undefined;
    let cancel = false;
    setAssetsHeld(null);
    api.get("/assets", { params: { pic_id: uid } })
      .then((r) => { if (!cancel) setAssetsHeld(Array.isArray(r.data) ? r.data : (r.data?.items || [])); })
      .catch(() => { if (!cancel) setAssetsHeld([]); });
    return () => { cancel = true; };
  }, [open, targetUserId, user?.id]);

  const computedPtkp = useMemo(() => computePtkp(form.marital_status, children, form.gender), [form.marital_status, children, form.gender]);
  const isMarried = form.marital_status === "Menikah";

  // When editing your own profile: only bank + emergency editable unless you are HR/Director.
  // When HR/Director opens another user's profile: everything editable.
  const canEditAll = isAdminEditingOther ? isAdmin : isAdmin;
  const isEditable = (meta) => canEditAll || USER_EDITABLE.has(meta.key);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      PROFILE_FIELDS_META.forEach((meta) => {
        payload[meta.key] = form[meta.key] || null;
      });
      if (!isMarried) {
        payload.spouse_name = null;
        payload.spouse_birth_date = null;
        payload.spouse_ktp = null;
      }
      payload.bank_admin_fee_accepted = !!form.bank_admin_fee_accepted;
      if (canEditAll) {
        payload.children = children
          .filter((c) => (c.name || "").trim())
          .map((c) => ({ name: c.name.trim(), birth_date: c.birth_date || null, ktp: c.ktp || null }));
        payload.assigned_project_ids = assignedProjectIds;
      }
      delete payload.ptkp_status;
      const { data } = await api.patch(profileEndpoint, payload);
      setProfile(data);
      const f = {};
      PROFILE_FIELDS_META.forEach((meta) => { f[meta.key] = data[meta.key] || ""; });
      f.bank_admin_fee_accepted = !!data.bank_admin_fee_accepted;
      setForm(f);
      setChildren(Array.isArray(data.children) ? data.children : []);
      toast.success(canEditAll ? ("Profil disimpan. PTKP: " + (data.ptkp_status || "-")) : "Data bank & kontak darurat disimpan");
      onOpenChange(false);
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const renderInput = (meta) => {
    const v = form[meta.key] || "";
    const editable = isEditable(meta);
    const onChange = (val) => setForm((s) => ({ ...s, [meta.key]: val }));
    if (meta.type === "user-picker") {
      // Combobox listing all personnel by name; store user_id
      const list = teamOptions.filter((u) => u.id !== targetUserId);
      return (
        <Select value={v || "__none__"} onValueChange={(val) => onChange(val === "__none__" ? "" : val)} disabled={!editable}>
          <SelectTrigger data-testid={`profile-${meta.key}`} className="rounded-none h-9 text-xs">
            <SelectValue placeholder={editable ? "Pilih atasan…" : "—"} />
          </SelectTrigger>
          <SelectContent className="rounded-none max-h-72">
            <SelectItem value="__none__" className="text-xs italic text-slate-500">— Tidak ada atasan —</SelectItem>
            {list.map((u) => (
              <SelectItem key={u.id} value={u.id} className="text-xs">
                {u.name} · {u.division}{u.division === "Project" ? ` ${u.sub_division}` : ""} Lv{u.level}{u.position ? ` — ${u.position}` : (u.role_title ? ` — ${u.role_title}` : "")}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }
    if (meta.type === "position-picker") {
      // Position select — options come from /api/positions managed in User Management
      const div = profile?.division;
      // Show positions matching the user's division first, then generic (no division)
      const sorted = [...positionOptions].sort((a, b) => {
        const aMatch = a.division === div ? 0 : (a.division ? 2 : 1);
        const bMatch = b.division === div ? 0 : (b.division ? 2 : 1);
        return aMatch - bMatch || (a.name || "").localeCompare(b.name || "");
      });
      return (
        <Select value={v || "__none__"} onValueChange={(val) => onChange(val === "__none__" ? "" : val)} disabled={!editable}>
          <SelectTrigger data-testid={`profile-${meta.key}`} className="rounded-none h-9 text-xs">
            <SelectValue placeholder={editable ? (meta.placeholder || "Pilih…") : "—"} />
          </SelectTrigger>
          <SelectContent className="rounded-none max-h-72">
            <SelectItem value="__none__" className="text-xs italic text-slate-500">— Tanpa Position —</SelectItem>
            {sorted.map((p) => (
              <SelectItem key={p.id} value={p.name} className="text-xs">
                {p.name}{p.division ? ` · ${p.division}` : ""}
              </SelectItem>
            ))}
            {sorted.length === 0 && (
              <SelectItem value="__empty__" disabled className="text-xs italic text-slate-400">Belum ada position — tambahkan di User Management → Positions</SelectItem>
            )}
          </SelectContent>
        </Select>
      );
    }
    if (meta.type === "date") {
      return <DatePicker value={v} onChange={editable ? onChange : () => {}} testid={`profile-${meta.key}`} placeholder="Pilih tanggal" disabled={!editable} />;
    }
    if (meta.type === "textarea") {
      return (
        <Textarea data-testid={`profile-${meta.key}`} value={v} onChange={(e) => onChange(e.target.value)} placeholder={meta.placeholder || ""} className="rounded-none text-xs" rows={2} disabled={!editable} />
      );
    }
    if (meta.type === "select") {
      const options = meta.options.map((o) => (typeof o === "string" ? { v: o, l: o } : o));
      const handleSelectChange = (val) => {
        if (!editable) return;
        if (meta.custom === "bank" && val && !isCompanyBank(val)) {
          setPendingBank(val);
          return;
        }
        onChange(val);
        if (meta.custom === "bank") {
          setForm((s) => ({ ...s, bank_admin_fee_accepted: isCompanyBank(val) ? false : true }));
        }
      };
      return (
        <Select value={v} onValueChange={handleSelectChange} disabled={!editable}>
          <SelectTrigger data-testid={`profile-${meta.key}`} className="rounded-none h-9 text-xs">
            <SelectValue placeholder={editable ? "Pilih…" : "—"} />
          </SelectTrigger>
          <SelectContent className="rounded-none max-h-72">
            {options.map((o) => <SelectItem key={o.v} value={o.v} className="text-xs">{o.l}</SelectItem>)}
          </SelectContent>
        </Select>
      );
    }
    if (meta.type === "city") {
      // Native datalist combobox: type-to-search across 500+ Indonesian cities.
      // Free entry allowed (fallback when city not in list).
      const listId = `city-list-${meta.key}`;
      return (
        <>
          <Input
            data-testid={`profile-${meta.key}`}
            value={v}
            onChange={(e) => onChange(e.target.value)}
            placeholder={meta.placeholder || "Ketik nama kota…"}
            className="rounded-none h-9 text-xs"
            disabled={!editable}
            list={listId}
            autoComplete="off"
          />
          <datalist id={listId}>
            {INDONESIA_CITIES.map((c) => <option key={c} value={c} />)}
          </datalist>
        </>
      );
    }
    return (
      <Input data-testid={`profile-${meta.key}`} value={v} onChange={(e) => onChange(e.target.value)} placeholder={meta.placeholder || ""} className="rounded-none h-9 text-xs" disabled={!editable} />
    );
  };

  const acceptBankFee = () => {
    if (!pendingBank) return;
    setForm((s) => ({ ...s, bank_name: pendingBank, bank_admin_fee_accepted: true }));
    setPendingBank(null);
    toast.info("Anda menyetujui biaya admin transfer antar bank");
  };
  const declineBankFee = () => { setPendingBank(null); toast.warning(companyBankNames.length ? `Silakan pilih bank yang terdaftar di company bank list (${feeFreeBankLabel})` : "Silakan pilih bank yang terdaftar di daftar company bank"); };

  const showField = (meta) => {
    if ((meta.key === "spouse_name" || meta.key === "spouse_birth_date" || meta.key === "spouse_ktp") && !isMarried) return false;
    return true;
  };

  const sectionOf = (key) => PROFILE_FIELDS_META.filter((m) => m.section === key && showField(m));
  const SectionHeader = ({ icon: Icon, title, subtitle, locked }) => (
    <div className="border-t border-slate-200 pt-3">
      <div className="mb-2 flex items-center gap-2">
        {Icon && <Icon size={14} className="text-slate-500" />}
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-2">
            {title}
            {locked && (<span className="inline-flex items-center gap-1 text-[9px] text-amber-700 normal-case font-normal"><Lock size={9} /> HR / Director only</span>)}
          </div>
          {subtitle && <div className="text-[10px] text-slate-400">{subtitle}</div>}
        </div>
      </div>
    </div>
  );

  const renderSection = (fields) => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3">
      {fields.map((meta) => (
        <div key={meta.key} className={meta.type === "textarea" ? "md:col-span-2" : ""}>
          <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{meta.label}</label>
          <div className="mt-1">{renderInput(meta)}</div>
          {meta.custom === "bank" && form.bank_name && !isCompanyBank(form.bank_name) && (
            <div className="mt-1 text-[10px] text-amber-700 inline-flex items-center gap-1">
              <AlertTriangle size={10} /> Biaya admin transfer dibebankan · {form.bank_admin_fee_accepted ? "disetujui ✓" : "belum disetujui"}
            </div>
          )}
          {meta.custom === "bank" && form.bank_name && isCompanyBank(form.bank_name) && (
            <div className="mt-1 text-[10px] text-emerald-700">✓ Bank perusahaan · tanpa biaya admin</div>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="rounded-none max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="dlg-profile">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <User size={20} className="text-[#FF5A1F]" /> {isAdminEditingOther ? `Profil · ${profile?.name || ""}` : "My Profile"}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              {isAdminEditingOther
                ? "Anda mengedit profil personil melalui akses Personnel Manage. Semua field dapat diubah."
                : canEditAll
                  ? "Data pribadi & keluarga. Status PTKP dihitung otomatis dari status pernikahan & jumlah anak."
                  : "Anda hanya dapat mengubah data bank, nomor rekening, dan kontak darurat. Data pribadi & keluarga hanya bisa diedit oleh HR / Director."}
            </DialogDescription>
          </DialogHeader>

          {profile && (
            <div className="space-y-4">
              {/* Read-only header */}
              <div className="bg-slate-50 border border-slate-200 p-3 grid grid-cols-1 md:grid-cols-4 gap-3 text-xs">
                <div><div className="overline text-slate-500 text-[10px]">Nama</div><div className="font-semibold text-slate-800">{profile.name}</div></div>
                <div><div className="overline text-slate-500 text-[10px]">Email</div><div className="text-slate-700 truncate">{profile.email}</div></div>
                <div><div className="overline text-slate-500 text-[10px]">Divisi</div><div className="flex items-center gap-1"><span className={`px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${DIVISION_COLOR[profile.division] || "bg-slate-600 text-white"}`}>{profile.division}</span><span className="text-slate-500">Lv{profile.level}</span></div></div>
                <div><div className="overline text-slate-500 text-[10px]">Sub Divisi</div><div className="text-slate-700">{profile.sub_division}</div></div>
              </div>

              {/* Structure / Organization — Role & Reports To */}
              <SectionHeader title="Struktur Organisasi" locked={!canEditAll} subtitle="Jabatan dan atasan langsung (diedit oleh HR / Director)" />
              {renderSection(sectionOf(SECTION_STRUCTURE))}

              {/* Personal */}
              <SectionHeader title="Data Pribadi" locked={!canEditAll} />
              {renderSection(sectionOf(SECTION_PERSONAL))}

              {/* Family (with children editor + PTKP inline) */}
              <SectionHeader title="Data Keluarga" locked={!canEditAll} />
              {renderSection(sectionOf(SECTION_FAMILY))}

              {/* Children editor — HR/Director only */}
              {canEditAll && (
                <div className="mt-3">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Data Anak</div>
                      <div className="text-[10px] text-slate-400">Nama, tanggal lahir & NIK setiap anak — mempengaruhi PTKP (maks 3 tanggungan)</div>
                    </div>
                    <button type="button" data-testid="btn-add-child" onClick={() => setChildren((s) => [...s, { name: "", birth_date: "", ktp: "" }])} className="text-[10px] uppercase tracking-wider font-semibold text-[#FF5A1F] hover:underline inline-flex items-center gap-1"><Plus size={12} /> Tambah Anak</button>
                  </div>
                  {children.length === 0 && (<div className="text-[11px] text-slate-400 italic border border-dashed border-slate-200 px-3 py-4 text-center">Belum ada data anak.</div>)}
                  {children.map((c, i) => (
                    <div key={c._key || `child-${i}`} className="grid grid-cols-[1fr_170px_1fr_36px] gap-2 mb-1 items-center" data-testid={`child-row-${i}`}>
                      <Input data-testid={`input-child-name-${i}`} value={c.name} onChange={(e) => setChildren((s) => s.map((it, idx) => idx === i ? { ...it, name: e.target.value } : it))} placeholder="Nama anak" className="rounded-none h-9 text-xs" />
                      <DatePicker value={c.birth_date} onChange={(v) => setChildren((s) => s.map((it, idx) => idx === i ? { ...it, birth_date: v } : it))} testid={`dp-child-birth-${i}`} placeholder="Tgl lahir" />
                      <Input data-testid={`input-child-ktp-${i}`} value={c.ktp || ""} onChange={(e) => setChildren((s) => s.map((it, idx) => idx === i ? { ...it, ktp: e.target.value } : it))} placeholder="NIK anak" className="rounded-none h-9 text-xs" />
                      <button type="button" data-testid={`btn-remove-child-${i}`} onClick={() => setChildren((s) => s.filter((_, idx) => idx !== i))} className="h-9 text-slate-400 hover:text-rose-600 flex items-center justify-center"><Trash2 size={14} /></button>
                    </div>
                  ))}
                </div>
              )}

              {/* PTKP auto */}
              <div className="border border-slate-200 bg-amber-50 px-3 py-2 flex items-center gap-3">
                <BadgeInfo size={16} className="text-amber-700 shrink-0" />
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-wider text-amber-800 font-semibold">Status PTKP (Otomatis)</div>
                  <div className="text-[11px] text-amber-900">Dari status pernikahan & jumlah anak (maks 3 tanggungan).</div>
                </div>
                <div className="font-display text-2xl font-bold text-[#0B1D3A] tabular-nums" data-testid="computed-ptkp">{computedPtkp}</div>
                {profile.ptkp_status && profile.ptkp_status !== computedPtkp && (
                  <div className="text-[10px] text-slate-500"><div>tersimpan:</div><div className="font-semibold">{profile.ptkp_status}</div></div>
                )}
              </div>

              {/* Education */}
              <SectionHeader title="Pendidikan" locked={!canEditAll} />
              {renderSection(sectionOf(SECTION_EDU))}

              {/* Penempatan Project (per-staff project access gating) */}
              <SectionHeader
                title="Penempatan Project Saat Ini"
                locked={!canEditAll}
                subtitle={
                  profile.level === 3
                    ? "Batasi akses staff hanya ke project yang dipilih. Jika kosong, staff mengikuti aturan default divisi."
                    : "Info penempatan project untuk personel ini (untuk staff Level 3 — pilihan mempengaruhi Project Management filter)."
                }
              />
              {(() => {
                const filtered = projectOptions.filter((p) => {
                  if (!projectFilter.trim()) return true;
                  const s = projectFilter.toLowerCase();
                  return (p.project_name || p.variation_order_name || p.wo_number || "").toLowerCase().includes(s) || (p.client || "").toLowerCase().includes(s);
                });
                const selectedProjects = projectOptions.filter((p) => assignedProjectIds.includes(p.id));
                return (
                  <div className="border border-slate-200 p-3 space-y-2" data-testid="profile-project-assignment">
                    {canEditAll && officePresets.length > 0 && (
                      <div className="flex items-center gap-2 pb-2 border-b border-slate-100">
                        <span className="overline text-slate-500 shrink-0">Preset Cepat:</span>
                        <div className="flex flex-wrap gap-1">
                          {officePresets.map((o) => {
                            const label = o.project_name || o.wo_number || "Office";
                            // Head Office → assign ALL billable projects.
                            // Office <Kota> → assign projects in the matching province/city.
                            let previewCount = 0;
                            let targetIds = [];
                            const nameLower = label.toLowerCase();
                            if (nameLower.includes("head")) {
                              targetIds = projectOptions.map((p) => p.id);
                            } else if (nameLower.includes("medan")) {
                              targetIds = projectOptions.filter((p) => (p.province || "").toLowerCase().includes("sumatera utara")).map((p) => p.id);
                            } else {
                              // Match by name substring against project city
                              const cityToken = nameLower.replace(/office\s+/i, "").trim();
                              if (cityToken) {
                                targetIds = projectOptions.filter((p) =>
                                  (p.city || "").toLowerCase().includes(cityToken) ||
                                  (p.province || "").toLowerCase().includes(cityToken)
                                ).map((p) => p.id);
                              }
                            }
                            previewCount = targetIds.length;
                            const isActive = targetIds.length > 0 && targetIds.every((id) => assignedProjectIds.includes(id)) && assignedProjectIds.length === targetIds.length;
                            return (
                              <button
                                key={o.id}
                                type="button"
                                data-testid={`profile-office-preset-${o.id}`}
                                onClick={() => setAssignedProjectIds(targetIds)}
                                className={`px-2 py-1 text-[10px] uppercase tracking-wider font-semibold border transition-colors ${
                                  isActive
                                    ? "bg-[#0B1D3A] text-white border-[#0B1D3A]"
                                    : "bg-white text-slate-700 border-slate-300 hover:border-[#0B1D3A] hover:text-[#0B1D3A]"
                                }`}
                              >
                                {label} · <span className="tabular-nums opacity-80">{previewCount}</span>
                              </button>
                            );
                          })}
                          {assignedProjectIds.length > 0 && (
                            <button
                              type="button"
                              data-testid="profile-office-preset-clear"
                              onClick={() => setAssignedProjectIds([])}
                              className="px-2 py-1 text-[10px] uppercase tracking-wider font-semibold border border-rose-300 text-rose-700 hover:bg-rose-50"
                            >
                              Clear
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                    {selectedProjects.length > 0 ? (
                      <div className="flex flex-wrap gap-1 pb-2 border-b border-slate-100">
                        {selectedProjects.map((p) => (
                          <span key={p.id} data-testid={`profile-project-chip-${p.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#FF5A1F]/10 border border-[#FF5A1F]/40 text-[10px] font-semibold text-[#0B1D3A]">
                            {p.project_name || p.variation_order_name || p.wo_number}
                            {canEditAll && (
                              <button
                                type="button"
                                data-testid={`profile-project-chip-remove-${p.id}`}
                                onClick={() => setAssignedProjectIds((s) => s.filter((id) => id !== p.id))}
                                className="text-slate-400 hover:text-rose-600"
                              >×</button>
                            )}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div className="text-[11px] text-slate-400 italic pb-2 border-b border-slate-100">
                        {profile.level === 3
                          ? (canEditAll ? "Belum ada project dipilih — staff akan melihat semua project sesuai aturan divisi." : "Belum ditugaskan ke project tertentu.")
                          : "Level 1/2 tidak dibatasi oleh penempatan project."}
                      </div>
                    )}
                    {canEditAll && (
                      <>
                        <Input
                          data-testid="profile-project-search"
                          value={projectFilter}
                          onChange={(e) => setProjectFilter(e.target.value)}
                          placeholder="Cari nama project atau klien…"
                          className="rounded-none h-8 text-xs"
                        />
                        <div className="max-h-[240px] overflow-y-auto border border-slate-100">
                          {filtered.length === 0 && (
                            <div className="text-[11px] text-slate-400 italic p-3 text-center">Tidak ada project aktif yang cocok.</div>
                          )}
                          {filtered.map((p) => {
                            const label = p.project_name || p.variation_order_name || p.wo_number || "(Untitled)";
                            const checked = assignedProjectIds.includes(p.id);
                            return (
                              <label
                                key={p.id}
                                data-testid={`profile-project-option-${p.id}`}
                                className={`flex items-start gap-2 px-2 py-1.5 border-b border-slate-100 cursor-pointer hover:bg-slate-50 ${checked ? "bg-[#FF5A1F]/5" : ""}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={(e) => {
                                    if (e.target.checked) setAssignedProjectIds((s) => [...s, p.id]);
                                    else setAssignedProjectIds((s) => s.filter((id) => id !== p.id));
                                  }}
                                  className="mt-0.5 accent-[#FF5A1F]"
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="text-xs font-semibold text-[#0B1D3A] truncate">{label}</div>
                                  <div className="text-[10px] text-slate-500 truncate">
                                    {p.client || "(no client)"} · {p.project_type?.replace(/_/g, " ")}
                                  </div>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                        <div className="flex items-center justify-between text-[10px] text-slate-500 pt-1">
                          <span>{assignedProjectIds.length} project dipilih · {projectOptions.length} project aktif tersedia</span>
                          {assignedProjectIds.length > 0 && (
                            <button
                              type="button"
                              data-testid="profile-project-clear"
                              onClick={() => setAssignedProjectIds([])}
                              className="text-rose-600 hover:underline uppercase tracking-wider font-semibold"
                            >Kosongkan Semua</button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })()}

              {/* Bank Accounts — multi entry */}
              <SectionHeader title="Data Bank & Rekening" subtitle="Anda dapat menyimpan lebih dari satu rekening. Yang pertama menjadi rekening utama transfer gaji." />
              <BankAccountsEditor
                endpoint={isAdminEditingOther ? `/admin/users/${targetUserId}/bank-accounts` : "/auth/bank-accounts"}
                canEdit={true}
              />

              {/* Emergency */}
              <div className="border-t border-slate-200 pt-3">
                <div className="mb-2 flex items-center gap-2">
                  <PhoneCall size={14} className="text-rose-600" />
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Kontak Darurat (Tidak Serumah)</div>
                    <div className="text-[10px] text-slate-400">Kontak yang tidak tinggal serumah dengan Anda — dapat Anda isi sendiri</div>
                  </div>
                </div>
                {renderSection(sectionOf(SECTION_EMERGENCY))}
              </div>

              {/* Notification Preferences — only for self, not admin editing others */}
              {!isAdminEditingOther && (
                <div className="border-t border-slate-200 pt-3" data-testid="section-notif-prefs">
                  <button
                    type="button"
                    data-testid="btn-toggle-notif-prefs"
                    onClick={() => setNotifPrefsOpen((s) => !s)}
                    className="w-full flex items-center justify-between text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold hover:text-[#0B1D3A]"
                  >
                    <span className="inline-flex items-center gap-1.5">
                      <Bell size={12} /> Preferensi Notifikasi
                      <span className="text-[9px] normal-case tracking-normal text-slate-400">— pilih jenis notifikasi yang mau kamu terima</span>
                    </span>
                    <ChevronDown size={14} className={`transition-transform ${notifPrefsOpen ? "rotate-180" : ""}`} />
                  </button>
                  {notifPrefsOpen && (
                    <div className="mt-2 space-y-3">
                      <NotificationPrefsSection open={notifPrefsOpen} />
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Pesan Internal</div>
                        <MessagingPrefsSection open={notifPrefsOpen} />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Session History — riwayat login/logout dengan IP + device */}
              <div className="border-t border-slate-200 pt-3" data-testid="section-session-history">
                <button
                  type="button"
                  data-testid="btn-toggle-session-history"
                  onClick={() => setSessionOpen((s) => !s)}
                  className="w-full flex items-center justify-between text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold hover:text-[#0B1D3A]"
                >
                  <span className="inline-flex items-center gap-1.5">
                    <Shield size={12} /> Riwayat Sesi (Login / Logout)
                    <span className="text-[9px] normal-case tracking-normal text-slate-400">— cek device & IP yang pernah akses akun</span>
                  </span>
                  <ChevronDown size={14} className={`transition-transform ${sessionOpen ? "rotate-180" : ""}`} />
                </button>
                {sessionOpen && (
                  <div className="mt-2">
                    <SessionHistorySection open={sessionOpen} targetUserId={isAdminEditingOther ? targetUserId : null} />
                  </div>
                )}
              </div>

              {/* Audit Log — admin viewing another user's profile */}
              {isAdminEditingOther && (
                <div className="border-t border-slate-200 pt-3" data-testid="section-audit-log">
                  <button
                    type="button"
                    data-testid="btn-toggle-audit"
                    onClick={() => setAuditOpen((s) => !s)}
                    className="w-full flex items-center justify-between text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold hover:text-[#0B1D3A]"
                  >
                    <span className="inline-flex items-center gap-1.5">
                      <History size={12} /> Audit Log Perubahan Profil {auditLogs.length > 0 && <span className="text-[9px] normal-case tracking-normal text-slate-400">({auditLogs.length} entri)</span>}
                    </span>
                    <ChevronDown size={14} className={`transition-transform ${auditOpen ? "rotate-180" : ""}`} />
                  </button>
                  {auditOpen && (
                    <div className="mt-2 border border-slate-200 max-h-64 overflow-y-auto" data-testid="audit-log-list">
                      {auditLogs.length === 0 ? (
                        <div className="text-[10px] text-slate-400 italic px-3 py-4 text-center">Belum ada perubahan tercatat</div>
                      ) : (
                        <table className="w-full text-[10px]">
                          <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider sticky top-0">
                            <tr>
                              <th className="text-left px-2 py-1">Waktu</th>
                              <th className="text-left px-2 py-1">Oleh</th>
                              <th className="text-left px-2 py-1">Kategori</th>
                              <th className="text-left px-2 py-1">Field</th>
                              <th className="text-left px-2 py-1">Sebelum</th>
                              <th className="text-left px-2 py-1">Sesudah</th>
                            </tr>
                          </thead>
                          <tbody>
                            {auditLogs.map((l) => (
                              <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50">
                                <td className="px-2 py-1 text-slate-500 whitespace-nowrap">{new Date(l.created_at).toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</td>
                                <td className="px-2 py-1 text-slate-700"><span className="font-semibold">{l.actor_name}</span><div className="text-[9px] text-slate-400">{l.actor_division} Lv{l.actor_level}</div></td>
                                <td className="px-2 py-1"><span className={`px-1 py-0.5 text-[9px] uppercase tracking-wider ${l.category === "salary" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700"}`}>{l.category}</span></td>
                                <td className="px-2 py-1 font-mono text-slate-700">{l.field}</td>
                                <td className="px-2 py-1 text-slate-500 max-w-[140px] truncate" title={JSON.stringify(l.old_value)}>{fmtAuditVal(l.old_value)}</td>
                                <td className="px-2 py-1 text-emerald-700 max-w-[140px] truncate" title={JSON.stringify(l.new_value)}>{fmtAuditVal(l.new_value)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* R232 (2026-10-20): Aset yang sedang dipinjam/dipegang
                  karyawan ini — read-only di profil (menu Data Aset kini di
                  grup Operasional & Aset). */}
              {assetsHeld !== null && (
                <div className="border-t border-slate-200 pt-3" data-testid="profile-assets-held">
                  <h3 className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 mb-2">Aset Dipinjam / Dipegang</h3>
                  {assetsHeld.length === 0 ? (
                    <div className="text-[11px] text-slate-400 italic" data-testid="profile-assets-empty">Tidak ada aset yang sedang dipegang karyawan ini.</div>
                  ) : (
                    <div className="space-y-1">
                      {assetsHeld.map((a) => (
                        <div key={a.id} className="flex items-center justify-between gap-2 text-[11px] border border-slate-200 px-2 py-1.5" data-testid={`profile-asset-${a.id}`}>
                          <span className="text-slate-800 font-medium truncate">{a.name || a.asset_name || "—"}</span>
                          <span className="text-[10px] text-slate-500 font-mono ml-2 shrink-0">{a.asset_code || a.code || ""}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div className="border-t border-slate-200 pt-3 flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap gap-2">
                  {!isAdminEditingOther && (
                    <Button data-testid="btn-open-salary" onClick={() => setOpenSalary(true)} variant="outline" className="rounded-none border-[#0B1D3A] text-[#0B1D3A] hover:bg-[#0B1D3A] hover:text-white inline-flex items-center gap-2"><Wallet size={14} /> View Salary</Button>
                  )}
                  {isAdminEditingOther && (
                    <Button data-testid="btn-open-admin-salary" onClick={() => setOpenSalary(true)} variant="outline" className="rounded-none border-[#0B1D3A] text-[#0B1D3A] hover:bg-[#0B1D3A] hover:text-white inline-flex items-center gap-2"><Wallet size={14} /> View / Edit Salary</Button>
                  )}
                  <Button data-testid="btn-print-profile" onClick={() => { document.body.classList.add("print-profile-only"); window.print(); setTimeout(() => document.body.classList.remove("print-profile-only"), 500); }} variant="outline" className="rounded-none border-slate-400 text-slate-700 hover:bg-slate-100 inline-flex items-center gap-2"><Printer size={14} /> Print / Export PDF</Button>
                  {/* 149-cw-r150: Deactivate / Reactivate account — matrix-gated (user_management.update).
                      Untuk kasus karyawan berhenti bekerja, HR/staff dgn perm ini bisa langsung
                      nonaktifkan tanpa harus lewat BOD Lv1. */}
                  {canDisableUser && targetUserId !== user?.id && (
                    profile?.is_active === false ? (
                      <Button
                        data-testid="btn-profile-enable-account"
                        variant="outline"
                        className="rounded-none border-emerald-400 text-emerald-700 hover:bg-emerald-50 inline-flex items-center gap-2"
                        onClick={async () => {
                          try {
                            await api.post(`/admin/users/${targetUserId}/enable`);
                            toast.success(`${profile?.name || "User"} diaktifkan kembali`);
                            setProfile((p) => p ? { ...p, is_active: true } : p);
                          } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Gagal aktifkan"); }
                        }}
                      ><UserCheck size={14} /> Aktifkan Akun</Button>
                    ) : (
                      <Button
                        data-testid="btn-profile-disable-account"
                        variant="outline"
                        className="rounded-none border-rose-400 text-rose-700 hover:bg-rose-50 inline-flex items-center gap-2"
                        onClick={() => setDisableDlg(true)}
                      ><UserX size={14} /> Nonaktifkan Akun</Button>
                    )
                  )}
                  {/* 149-cw-r173: Hapus Akun permanen — untuk staff yg datanya memang
                      harus dihilangkan (bukan sekadar nonaktif). Butuh konfirmasi. */}
                  {canDeleteUser && targetUserId !== user?.id && (
                    <Button
                      data-testid="btn-profile-delete-account"
                      variant="outline"
                      className="rounded-none border-rose-600 bg-rose-600 text-white hover:bg-rose-700 inline-flex items-center gap-2"
                      onClick={() => setDeleteDlg(true)}
                    ><Trash2 size={14} /> Hapus Akun</Button>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-none">Tutup</Button>
                  <Button data-testid="btn-save-profile" onClick={save} disabled={saving} className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13] text-white"><Save size={14} className="mr-1" /> {saving ? "Menyimpan…" : "Simpan"}</Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <SalaryDialog open={openSalary} onOpenChange={setOpenSalary} targetUserId={salaryTargetId} />

      {/* 149-cw-r151: Dialog nonaktifkan akun dgn alasan wajib. */}
      <DisableAccountDialog
        open={disableDlg}
        onOpenChange={setDisableDlg}
        userName={profile?.name}
        busy={disableBusy}
        onConfirm={async (reason) => {
          setDisableBusy(true);
          try {
            await api.post(`/admin/users/${targetUserId}/disable`, { reason });
            toast.success(`${profile?.name || "User"} dinonaktifkan · alasan: ${reason}`);
            setProfile((p) => p ? { ...p, is_active: false, disabled_reason: reason } : p);
            setDisableDlg(false);
          } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Gagal nonaktifkan"); }
          finally { setDisableBusy(false); }
        }}
      />

      {/* 149-cw-r173: Konfirmasi hapus akun permanen. */}
      <AlertDialog open={deleteDlg} onOpenChange={setDeleteDlg}>
        <AlertDialogContent className="rounded-none" data-testid="dlg-delete-account-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-xl flex items-center gap-2"><Trash2 size={20} className="text-rose-600" /> Hapus Akun Staff?</AlertDialogTitle>
            <AlertDialogDescription>
              Akun <span className="font-semibold">{profile?.name || "user ini"}</span> akan dihapus <span className="font-semibold text-rose-700">PERMANEN</span> dan tidak bisa dikembalikan. Kalau karyawan hanya berhenti bekerja, gunakan <span className="font-semibold">Nonaktifkan Akun</span> supaya riwayat datanya tetap tersimpan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none" data-testid="btn-delete-account-cancel">Batal</AlertDialogCancel>
            <AlertDialogAction
              data-testid="btn-delete-account-confirm"
              disabled={deleteBusy}
              className="rounded-none bg-rose-600 hover:bg-rose-700 text-white"
              onClick={async (e) => {
                e.preventDefault();
                setDeleteBusy(true);
                try {
                  await api.delete(`/admin/users/${targetUserId}`);
                  toast.success(`Akun ${profile?.name || "user"} dihapus permanen`);
                  setDeleteDlg(false);
                  onOpenChange(false);
                } catch (err) { toast.error(formatDetail(err.response?.data?.detail) || "Gagal menghapus akun"); }
                finally { setDeleteBusy(false); }
              }}
            >{deleteBusy ? "Menghapus…" : "Ya, Hapus Permanen"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!pendingBank} onOpenChange={(o) => { if (!o) setPendingBank(null); }}>
        <AlertDialogContent className="rounded-none" data-testid="dlg-bank-fee-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-xl flex items-center gap-2"><AlertTriangle size={20} className="text-amber-500" /> Konfirmasi Bank Non-Perusahaan</AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-slate-700 space-y-2">
              <div>Anda memilih <span className="font-semibold text-[#0B1D3A]">{pendingBank}</span>.</div>
              {companyBankNames.length > 0 ? (
                <div>Bank perusahaan yang tanpa biaya admin: <span className="font-semibold">{companyBankNames.join(", ")}</span>. Transfer ke bank di luar daftar tersebut akan dikenai biaya admin.</div>
              ) : (
                <div className="text-amber-700">Belum ada daftar bank perusahaan yang dikonfigurasi. Hubungi BOD/FAT untuk mengatur di Settings → Company Bank Accounts.</div>
              )}
              <div className="pt-1 font-semibold text-slate-800">Anda bersedia biaya admin dibebankan karena berbeda dengan bank perusahaan?</div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="btn-bank-decline" onClick={declineBankFee} className="rounded-none">Tidak Bersedia</AlertDialogCancel>
            <AlertDialogAction data-testid="btn-bank-accept" onClick={acceptBankFee} className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13] text-white">Saya Bersedia</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
